"use client";

import { useTranslation } from "react-i18next";
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useParams, useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { InferType, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { IChatResponse, IMessageResponse } from "@finbackoffice/clientbff-client";
import { ChatContext, ClientBFFContext, useSearchParamsQuery } from "@finbackoffice/site-core";
import { sortById } from "@finbackoffice/fe-core";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Translate from "components/base/translate/Translate";
import Button from "components/base/button/Button";
import Loading from "components/base/loading/Loading";
import { useDate, DEFAULT_DATE_FORMAT } from "hooks";
import { Chars } from "utils/strings";
import { Svg } from "components/base/svg/Svg";
import { ConfigContextV2 } from "contexts";
import styles from "../account-messages.module.sass";

type IChatState = {
    loading: boolean;
    data: (IChatResponse & { items: IMessageResponse[] }) | null;
};

export type IReplyMessageForm = InferType<typeof replyMessageValidationSchema>;

const replyMessageValidationSchema = object({
    message: string().required(),
});

const Chat: FC = () => {
    const client = useContext(ClientBFFContext);
    const { removeMessage, getUnreadMessagesByChatId } = useContext(ChatContext);
    const { siteMessagingConfig } = useContext(ConfigContextV2);
    const router = useRouter();
    const params = useParams();
    const { query } = useSearchParamsQuery();
    const { id: queryId } = query;
    const id = useMemo(() => {
        if (queryId) {
            return queryId;
        }
        return params.id;
    }, [queryId, params.id]);
    const { formatDate } = useDate();
    const { t } = useTranslation();
    const [chat, setChat] = useState<IChatState>({
        loading: false,
        data: null,
    });
    const unreadMessages = useMemo(
        () => (chat.data?.id ? getUnreadMessagesByChatId(chat.data.id) : []),
        [chat.data?.id, getUnreadMessagesByChatId],
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { isDirty, isSubmitting, isValid },
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(replyMessageValidationSchema),
        defaultValues: {
            message: "",
        },
    });

    const loadChat = useCallback(async () => {
        setChat((state) => ({
            ...state,
            loading: true,
        }));
        try {
            const response = await client.queryMessage(parseInt(id as string));

            if (response.chat) {
                setChat({ loading: false, data: { ...response.chat, items: response.items } });
            }
        } catch (_err: unknown) {
            setChat((state) => ({
                ...state,
                loading: false,
            }));
            router.back();
        }
    }, [client, id, router]);

    useEffect(() => {
        void loadChat();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!!unreadMessages.length && chat.data) {
            setChat({
                ...chat,
                data: { ...chat.data, items: [...chat.data.items, ...unreadMessages] },
            });
            removeMessage(
                chat.data.id,
                unreadMessages.map((msg) => msg.id),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unreadMessages]);

    const onSubmit = async (data: IReplyMessageForm) => {
        if (isDirty && !isSubmitting && isValid && chat.data) {
            setChat((state) => ({
                ...state,
                loading: true,
            }));
            try {
                const response = await client.createMessage(chat.data.id, {
                    message: data.message,
                });
                if (response && chat.data) {
                    reset();
                    await loadChat();
                }
            } catch (error) {
                console.error(error);
            } finally {
                setChat((state) => ({
                    ...state,
                    loading: false,
                }));
            }
        }
    };

    const onRemoveClick = async () => {
        if (chat.data) {
            try {
                await client.deleteChat(chat.data.id);

                router.back();
            } catch (error) {
                console.error(error);
            }
        }
    };

    const onStarrClick = async () => {
        if (chat.data) {
            try {
                const response = await client.updateChat(chat.data.id, {
                    is_starred: !chat.data?.is_starred,
                });

                if (response) {
                    await loadChat();
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleBackClick = useCallback(() => {
        router.back();
    }, [router]);

    if (!chat || !chat.data?.items.length) return null;

    return (
        <div className={styles.messages}>
            <div className={styles.chatHeader}>
                <div onClick={handleBackClick}>
                    <i className={styles.back} />
                    <span>
                        <Translate
                            tid="account_messages_subject"
                            namespace={TranslationScopes.UserAccount}
                        />
                        :{Chars.nbsp}
                        <span>{chat.data.topic}</span>
                    </span>
                </div>
                <Button type="button" disabled className={styles.delete} onClick={onRemoveClick}>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/message-bin.svg`}
                        className="svg-icon"
                    />
                </Button>
                <Button
                    type="button"
                    className={classNames(styles.starred, chat.data.is_starred && styles.isStarred)}
                    onClick={onStarrClick}>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/messages-starred.svg`}
                        className="svg-icon"
                    />
                </Button>
            </div>
            {chat.loading && <Loading />}
            <div className={styles.chat}>
                {chat.data.items.sort(sortById).map((message) => (
                    <div key={message.id} className={styles.message}>
                        <div className={styles.fromDiv}>
                            <span>
                                <Translate
                                    tid="account_messages_from"
                                    namespace={TranslationScopes.UserAccount}
                                />
                                :{Chars.nbsp}
                                <strong>
                                    {message.author === "User" ? (
                                        <Translate
                                            tid="account_messages_you"
                                            namespace={TranslationScopes.UserAccount}
                                        />
                                    ) : (
                                        message.author
                                    )}
                                </strong>
                            </span>
                            <span className={styles.date}>
                                {formatDate(
                                    new Date(message.created_at),
                                    `${DEFAULT_DATE_FORMAT} - hh:mm b`,
                                    false,
                                )}
                            </span>
                        </div>
                        <Translate text={message.message} dangerous>
                            <p />
                        </Translate>
                    </div>
                ))}
                <form className={styles.replyMsg} onSubmit={handleSubmit(onSubmit)}>
                    <textarea
                        disabled={chat.data.no_reply}
                        placeholder={t(
                            chat.data.no_reply
                                ? "account_messages_noReplyPlaceholder"
                                : "account_messages_typeMessagePlaceholder",
                            { ns: TranslationScopes.UserAccount },
                        )}
                        {...register("message")}
                        id="message"
                    />
                    {!chat.data.no_reply && (
                        <div className={styles.btnContainer}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => reset()}
                                disabled={!isDirty}>
                                <Translate tid="base_reset" />
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={
                                    !isDirty ||
                                    isSubmitting ||
                                    !isValid ||
                                    !siteMessagingConfig?.enabled
                                }>
                                <Translate
                                    tid="account_messages_send"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </Button>
                        </div>
                    )}
                    {!siteMessagingConfig?.enabled && (
                        <div className={styles.messagingTemporarilyDisabledMsg}>
                            <Translate
                                tid="account_menu_messages_disabled"
                                namespace={TranslationScopes.UserAccount}
                            />
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default Chat;
